// LoginForm.jsx
import React, { useState, useContext } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import { Formik, Field, Form } from 'formik';
import { Button, notification, Icon } from 'antd';
import Loader from '../Loader/Loader';
import { AntInput, AntPassword } from '../AntField';
import { API } from '../../config/config';
import { setToken } from '../../config/token';
import { Utils } from '../../context/UtilsContext';

const loginStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@400;600;700;800;900&display=swap');

  @keyframes wims-fadein {
    from { opacity: 0; transform: translateY(30px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes wims-logo-pop {
    0%   { transform: scale(0.5); opacity: 0; }
    65%  { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes wims-lock {
    0%, 85%, 100% { transform: rotate(0deg) scale(1); }
    88%  { transform: rotate(-14deg) scale(1.12); }
    92%  { transform: rotate(14deg) scale(1.12); }
    95%  { transform: rotate(-7deg); }
    98%  { transform: rotate(7deg); }
  }
  @keyframes wims-float {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-4px); }
  }
  @keyframes wims-beat {
    0%, 100% { transform: scale(1); }
    50%       { transform: scale(1.2); }
  }
  @keyframes wims-spin {
    to { transform: rotate(360deg); }
  }
  @keyframes wims-glow {
    0%, 100% { box-shadow: 0 0 0 0 var(--gc, rgba(80,171,233,0.5)); }
    50%       { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
  }
  @keyframes wims-bar {
    0%   { background-position: 0% center; }
    100% { background-position: 200% center; }
  }
  @keyframes wims-orb1 {
    0%,100% { transform: translate(0,0) scale(1); }
    40%     { transform: translate(40px,-30px) scale(1.08); }
    70%     { transform: translate(-20px,20px) scale(0.95); }
  }
  @keyframes wims-orb2 {
    0%,100% { transform: translate(0,0) scale(1); }
    35%     { transform: translate(-35px,25px) scale(1.06); }
    70%     { transform: translate(25px,-18px) scale(0.96); }
  }
  @keyframes wims-field-in {
    from { opacity: 0; transform: translateX(-12px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  /* ── page ── */
  html, body, #root {
    margin: 0 !important;
    padding: 0 !important;
    height: 100%;
    overflow-x: hidden;
  }
  .wims-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(150deg, #1a4d8c 0%, #2c5f9e 30%, #50ABE9 65%, #3e8bc9 100%);
    position: relative;
    overflow: hidden;
    font-family: 'Exo 2', sans-serif;
  }
  .wims-page::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(255,255,255,0.09) 1.5px, transparent 1.5px);
    background-size: 32px 32px;
    pointer-events: none;
  }
  .wims-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(72px);
  }
  .wims-orb-1 {
    width: 500px; height: 500px;
    background: rgba(255,255,255,0.09);
    top: -160px; left: -160px;
    animation: wims-orb1 14s ease-in-out infinite;
  }
  .wims-orb-2 {
    width: 380px; height: 380px;
    background: rgba(19,194,194,0.11);
    bottom: -100px; right: -100px;
    animation: wims-orb2 11s ease-in-out infinite;
  }

  /* ── split card ── */
  .wims-card {
    position: relative;
    z-index: 1;
    display: flex;
    width: 820px;
    max-width: calc(100vw - 32px);
    min-height: 480px;
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 32px 80px rgba(0,0,0,0.28), 0 0 0 1px rgba(255,255,255,0.12);
    animation: wims-fadein 0.6s cubic-bezier(.22,1,.36,1) both;
  }

  /* LEFT panel — branding */
  .wims-panel-left {
    flex: 1;
    background: linear-gradient(160deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.32) 100%);
    backdrop-filter: blur(10px);
    padding: 52px 40px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-right: 1px solid rgba(255,255,255,0.1);
  }
  .wims-brand-top { display: flex; flex-direction: column; align-items: flex-start; gap: 20px; }
  .wims-brand-logo {
    width: 64px; height: 64px;
    border-radius: 18px;
    background: rgba(255,255,255,0.15);
    border: 2px solid rgba(255,255,255,0.25);
    display: flex; align-items: center; justify-content: center;
    --gc: rgba(255,255,255,0.4);
    animation: wims-glow 2.8s ease-in-out infinite, wims-lock 5s ease-in-out infinite;
  }
  .wims-brand-name {
    font-size: 38px;
    font-weight: 900;
    color: #fff;
    letter-spacing: 4px;
    line-height: 1;
    text-shadow: 0 2px 20px rgba(0,0,0,0.3);
  }
  .wims-brand-desc {
    font-size: 11px;
    color: rgba(255,255,255,0.65);
    letter-spacing: 2px;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 1.6;
    max-width: 200px;
  }
  .wims-brand-pills {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .wims-pill {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: rgba(255,255,255,0.75);
    font-weight: 500;
  }
  .wims-pill-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #50ABE9;
    box-shadow: 0 0 8px #50ABE9;
    flex-shrink: 0;
  }
  .wims-brand-footer {
    font-size: 11px;
    color: rgba(255,255,255,0.35);
    letter-spacing: 0.5px;
  }

  /* RIGHT panel — form */
  .wims-panel-right {
    width: 380px;
    flex-shrink: 0;
    background: #fff;
    padding: 52px 44px 40px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .wims-top-bar {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #50ABE9, #2c5f9e, #1a4d8c, #50ABE9);
    background-size: 200% auto;
    animation: wims-bar 3s linear infinite;
  }

  .wims-form-title {
    font-size: 22px;
    font-weight: 800;
    color: #1a1a2e;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  .wims-form-sub {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 32px;
    font-weight: 500;
  }

  .wims-field-wrap {
    margin-bottom: 20px;
    animation: wims-field-in 0.4s ease both;
  }
  .wims-field-wrap:nth-child(1) { animation-delay: 0.2s; }
  .wims-field-wrap:nth-child(2) { animation-delay: 0.3s; }

  .wims-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #434343;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    margin-bottom: 7px;
  }
  .wims-label-icon {
    width: 24px; height: 24px;
    border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .wims-label-icon.blue {
    background: #e6f7ff;
    --gc: rgba(80,171,233,0.28);
    animation: wims-float 3s ease-in-out infinite, wims-glow 2.6s ease-in-out infinite;
  }
  .wims-label-icon.pink {
    background: #fff0f6;
    --gc: rgba(80,171,233,0.28);
    animation: wims-beat 1.8s ease-in-out infinite, wims-glow 2.6s ease-in-out infinite;
  }

  .wims-btn {
    height: 48px !important;
    border-radius: 12px !important;
    font-weight: 800 !important;
    font-size: 13px !important;
    letter-spacing: 2.5px !important;
    font-family: 'Exo 2', sans-serif !important;
    background: linear-gradient(135deg, #50ABE9 0%, #2c5f9e 100%) !important;
    border: none !important;
    box-shadow: 0 6px 20px rgba(80,171,233,0.4) !important;
    transition: all 0.2s ease !important;
    margin-top: 8px;
  }
  .wims-btn:hover:not([disabled]) {
    transform: translateY(-2px) !important;
    box-shadow: 0 10px 28px rgba(80,171,233,0.5) !important;
  }
  .wims-btn:active:not([disabled]) {
    transform: translateY(0) !important;
  }
  .wims-btn[disabled] { opacity: 0.6 !important; }

  .wims-secure {
    margin-top: 20px;
    text-align: center;
    font-size: 11px;
    color: #bfbfbf;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }
  .wims-secure .anticon {
    color: #52c41a;
    animation: wims-spin 6s linear infinite;
  }

  @media (max-width: 680px) {
    .wims-panel-left { display: none; }
    .wims-panel-right { width: 100%; padding: 44px 32px 36px; }
    .wims-card { width: 100%; min-height: unset; border-radius: 18px; }
  }
`;

const createCookie = (name) => {
  const date = new Date();
  date.setTime(date.getTime() + (2 * 60 * 1000));
  const expires = `expires=${date.toGMTString()}`;
  document.cookie = `${name}=1; ${expires}; path=/;`;
}

const checkCookie = (name) => {
  const { cookie } = document;
  const doesExist = cookie.split('; ').some((val) => val.indexOf(name) !== -1);
  return doesExist;
}

const SubmitHandler = (
  values,
  setSubmitting,
  setValues,
  submitCount,
  addSubmitCount,
  setLoading,
  setLoginSuccess,
) => {
  if ((submitCount[values.username] && submitCount[values.username] > 2) || checkCookie(values.username)) {
    notification.error({
      message: 'You have been blocked for a few minutes!',
    });
    setSubmitting(false);

    if (submitCount[values.username] === 3) {
      delete submitCount[values.username];
      createCookie(values.username);
    }
  } else {
    setLoading(true);
    axios.post(`${API}login/wims`, values)
      .then((res) => {
        setToken(res.data.access_token);
        setLoading(false);
        setLoginSuccess(true);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          notification.error({
            message: 'Invalid username or password',
          });
          setValues({ username: values.username, password: '' });
          addSubmitCount((val) => {
            const user = val[values.username];
            return {
              ...val,
              [values.username]: user ? user + 1 : 1,
            }
          })
        } else {
          notification.error({
            message: 'NO RESPONSE FROM THE SERVER!!',
          });
        }
        setSubmitting(false);
        setLoading(false);
      })
  }
}

const validate = Yup.object().shape({
  username: Yup.string()
    .required('Username is required')
    .min(2, 'Username should be atleast 2 characters')
    .max(12, 'Username should not exceed 12 characters'),
  password: Yup.string()
    .required('Password is required')
    .min(3, 'Password should be atleast 3 characters')
    .max(12, 'Password should not exceed 12 characters'),
})

function LoginForm({ setLoginSuccess }) {
  const defaultValues = {
    username: '',
    password: '',
  }
  const { loading, setLoading } = useContext(Utils);
  const [submitCount, addSubmitCount] = useState({});
  
  return (
    <div className="wims-page">
      <style>{loginStyles}</style>
      <div className="wims-orb wims-orb-1" />
      <div className="wims-orb wims-orb-2" />

      {loading && <Loader />}

      <div className="wims-card">
        <div className="wims-top-bar" />

        {/* ── Left Branding Panel ── */}
        <div className="wims-panel-left">
          <div className="wims-brand-top">
            <div className="wims-brand-logo">
              <Icon type="database" style={{ fontSize: 28, color: '#fff' }} />
            </div>
            <div>
              <div className="wims-brand-name">WIMS</div>
              <div className="wims-brand-desc">Warehouse Inventory Management System</div>
            </div>
          </div>
          <div className="wims-brand-pills">
            <div className="wims-pill"><div className="wims-pill-dot" />Real-time Inventory Tracking</div>
            <div className="wims-pill"><div className="wims-pill-dot" />Stock Level Monitoring</div>
            <div className="wims-pill"><div className="wims-pill-dot" />Item Location Management</div>
            <div className="wims-pill"><div className="wims-pill-dot" />Automated Stock Alerts</div>
          </div>
          <div className="wims-brand-footer">
            © {new Date().getFullYear()} WIMS · All rights reserved
          </div>
        </div>

        {/* ── Right Form Panel ── */}
        <div className="wims-panel-right">
          <div className="wims-form-title">Welcome back 👋</div>
          <div className="wims-form-sub">Sign in to your inventory account</div>

          <Formik
            validationSchema={validate}
            onSubmit={(values, { setSubmitting, setValues }) => (
              SubmitHandler(values, setSubmitting, setValues, submitCount, addSubmitCount, setLoading, setLoginSuccess)
            )}
            initialValues={defaultValues}
          >
            {({ values, isSubmitting }) => (
              <Form>
                <div className="wims-field-wrap">
                  <div className="wims-label">
                    <div className="wims-label-icon blue">
                      <Icon type="user" style={{ fontSize: 12, color: '#50ABE9' }} />
                    </div>
                    Username
                  </div>
                  <Field
                    component={AntInput}
                    name="username"
                    value={values.username}
                    autoComplete="off"
                    size="large"
                    placeholder="Enter your username"
                    hasFeedback
                    autoFocus
                  />
                </div>

                <div className="wims-field-wrap">
                  <div className="wims-label">
                    <div className="wims-label-icon pink">
                      <Icon type="lock" style={{ fontSize: 12, color: '#50ABE9' }} />
                    </div>
                    Password
                  </div>
                  <Field
                    component={AntPassword}
                    name="password"
                    value={values.password}
                    autoComplete="off"
                    size="large"
                    placeholder="Enter your password"
                    hasFeedback
                  />
                </div>

                <Button
                  className="wims-btn"
                  size="large"
                  type="primary"
                  htmlType="submit"
                  disabled={isSubmitting}
                  block
                >
                  <Icon type={isSubmitting ? 'loading' : 'login'} style={{ marginRight: 8 }} />
                  {isSubmitting ? 'SIGNING IN...' : 'SIGN IN'}
                </Button>

                <div className="wims-secure">
                  <Icon type="safety-certificate" />
                  Secure login · encrypted connection
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default LoginForm